import { useState, useEffect, useCallback, useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { motion, AnimatePresence, useInView, useScroll, useTransform } from "framer-motion";
import { ChevronLeft, ChevronRight, X, ExternalLink } from "lucide-react";

// Haptic feedback utility
const triggerHaptic = (pattern: number | number[] = 10) => {
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    navigator.vibrate(pattern);
  }
};

// Portfolio website screenshots data
const portfolioSites = [
  {
    name: "Zorba's Greek Kitchen",
    type: "Restaurant",
    image: "/portfolio/zorbas-greek.png",
  },
  {
    name: "Apex Plumbing",
    type: "Service Business",
    image: "/portfolio/apex-plumbing.png",
  },
  {
    name: "Los Generales",
    type: "Mexican Restaurant",
    image: "/portfolio/los-generales.png",
  },
  {
    name: "Mama Tana",
    type: "Salvadoran Restaurant",
    image: "/portfolio/mama-tana.png",
  },
  {
    name: "Taqueria La Fiesta",
    type: "Mexican Street Food",
    image: "/portfolio/taqueria-la-fiesta.png",
  },
  {
    name: "Pretzel Bakery",
    type: "Bakery",
    image: "/portfolio/pretzels-bakery.png",
  },
];

// Lightbox Modal Component
const Lightbox = ({ 
  site, 
  onClose,
  onNext,
  onPrev,
}: { 
  site: typeof portfolioSites[0] | null; 
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
}) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onNext, onPrev]);

  if (!site) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-md p-4 md:p-8"
    >
      {/* Navigation arrows */}
      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-5xl w-full max-h-[85vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0a] shadow-2xl"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Image */}
        <div className="overflow-auto max-h-[calc(85vh-72px)]">
          <img
            src={site.image}
            alt={`${site.name} website screenshot`}
            className="w-full h-auto"
          />
        </div>

        {/* Info bar */}
        <div className="p-4 border-t border-white/10 bg-[#0a0a0a] flex items-center justify-between">
          <div>
            <h3 className="text-white font-semibold text-lg">{site.name}</h3>
            <p className="text-white/50 text-sm">{site.type}</p>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors">
            <ExternalLink className="w-4 h-4" />
            View Site
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const AUTO_PLAY_DURATION = 4000;

export const PortfolioShowcase = () => {
  const { t } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedSite, setSelectedSite] = useState<typeof portfolioSites[0] | null>(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  
  // Scroll animation refs
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  
  // Parallax scroll effect
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [30, -30]);

  const goToSlide = useCallback((index: number) => {
    const newIndex = (index + portfolioSites.length) % portfolioSites.length;
    setActiveIndex(newIndex);
    triggerHaptic(5); // Light haptic on slide change
  }, []);

  const nextSlide = useCallback(() => goToSlide(activeIndex + 1), [activeIndex, goToSlide]);
  const prevSlide = useCallback(() => goToSlide(activeIndex - 1), [activeIndex, goToSlide]);

  // Handle swipe gestures with haptic feedback
  const handleDragEnd = useCallback((event: any, info: { offset: { x: number }; velocity: { x: number } }) => {
    const swipeThreshold = 50;
    const velocityThreshold = 500;
    
    if (info.offset.x < -swipeThreshold || info.velocity.x < -velocityThreshold) {
      triggerHaptic(15); // Medium haptic on swipe
      nextSlide();
    } else if (info.offset.x > swipeThreshold || info.velocity.x > velocityThreshold) {
      triggerHaptic(15);
      prevSlide();
    }
    setIsDragging(false);
  }, [nextSlide, prevSlide]);

  // Auto-play with progress
  useEffect(() => {
    if (!isAutoPlaying || selectedSite || isDragging) {
      setProgress(0);
      return;
    }
    
    const startTime = Date.now();
    const animationFrame = () => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / AUTO_PLAY_DURATION) * 100, 100);
      setProgress(newProgress);
      
      if (elapsed >= AUTO_PLAY_DURATION) {
        nextSlide();
        return;
      }
      requestAnimationFrame(animationFrame);
    };
    
    const frameId = requestAnimationFrame(animationFrame);
    return () => cancelAnimationFrame(frameId);
  }, [isAutoPlaying, activeIndex, selectedSite, isDragging, nextSlide]);

  // Reset progress on manual navigation
  const handleManualNavigation = useCallback((index: number) => {
    setProgress(0);
    goToSlide(index);
  }, [goToSlide]);

  const openLightbox = (site: typeof portfolioSites[0]) => {
    triggerHaptic([10, 50, 10]); // Double tap haptic pattern
    setSelectedSite(site);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedSite(null);
    document.body.style.overflow = '';
  };

  const lightboxNext = () => {
    const currentIdx = portfolioSites.findIndex(s => s.name === selectedSite?.name);
    const nextIdx = (currentIdx + 1) % portfolioSites.length;
    setSelectedSite(portfolioSites[nextIdx]);
  };

  const lightboxPrev = () => {
    const currentIdx = portfolioSites.findIndex(s => s.name === selectedSite?.name);
    const prevIdx = (currentIdx - 1 + portfolioSites.length) % portfolioSites.length;
    setSelectedSite(portfolioSites[prevIdx]);
  };

  // Get visible cards (previous, current, next)
  const getCardStyle = (index: number) => {
    const diff = index - activeIndex;
    const wrappedDiff = ((diff + portfolioSites.length + Math.floor(portfolioSites.length / 2)) % portfolioSites.length) - Math.floor(portfolioSites.length / 2);
    
    if (wrappedDiff === 0) {
      return {
        x: 0,
        scale: 1,
        rotateY: 0,
        zIndex: 10,
        opacity: 1,
        filter: "brightness(1)",
      };
    } else if (wrappedDiff === 1 || wrappedDiff === -portfolioSites.length + 1) {
      return {
        x: "60%",
        scale: 0.85,
        rotateY: -15,
        zIndex: 5,
        opacity: 0.7,
        filter: "brightness(0.6)",
      };
    } else if (wrappedDiff === -1 || wrappedDiff === portfolioSites.length - 1) {
      return {
        x: "-60%",
        scale: 0.85,
        rotateY: 15,
        zIndex: 5,
        opacity: 0.7,
        filter: "brightness(0.6)",
      };
    } else if (wrappedDiff === 2 || wrappedDiff === -portfolioSites.length + 2) {
      return {
        x: "110%",
        scale: 0.7,
        rotateY: -25,
        zIndex: 2,
        opacity: 0.4,
        filter: "brightness(0.4)",
      };
    } else if (wrappedDiff === -2 || wrappedDiff === portfolioSites.length - 2) {
      return {
        x: "-110%",
        scale: 0.7,
        rotateY: 25,
        zIndex: 2,
        opacity: 0.4,
        filter: "brightness(0.4)",
      };
    }
    return {
      x: wrappedDiff > 0 ? "150%" : "-150%",
      scale: 0.5,
      rotateY: wrappedDiff > 0 ? -30 : 30,
      zIndex: 1,
      opacity: 0,
      filter: "brightness(0.3)",
    };
  };

  return (
    <motion.div
      ref={sectionRef}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
    >
      <motion.div 
        className="text-center mb-12"
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
        transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      >
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
          {t("dashboard.portfolioTitle")}
        </h2>
        <p className="text-lg text-white/40">
          {t("dashboard.portfolioDescription")}
        </p>
      </motion.div>

      {/* 3D Spotlight Carousel */}
      <motion.div 
        className="relative h-[320px] md:h-[420px] overflow-hidden"
        onMouseEnter={() => setIsAutoPlaying(false)}
        onMouseLeave={() => setIsAutoPlaying(true)}
        style={{ perspective: "1200px" }}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.7, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Swipe gesture layer for mobile - pointer-events-none allows taps to pass through */}
        <motion.div
          className="absolute inset-0 z-20 md:hidden touch-pan-y pointer-events-none"
          style={{ touchAction: "pan-y" }}
        />

        {/* Cards container with parallax */}
        <motion.div 
          className="absolute inset-0 flex items-center justify-center"
          style={{ y: parallaxY }}
        >
          {portfolioSites.map((site, index) => {
            const style = getCardStyle(index);
            const isActive = index === activeIndex;
            
            return (
              <motion.div
                key={site.name}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.2}
                onDragStart={() => setIsDragging(true)}
                onDragEnd={handleDragEnd}
                animate={{
                  x: style.x,
                  scale: style.scale,
                  rotateY: style.rotateY,
                  zIndex: style.zIndex,
                  opacity: style.opacity,
                }}
                transition={{ 
                  type: "spring", 
                  stiffness: 300, 
                  damping: 30,
                  opacity: { duration: 0.2 }
                }}
                onClick={() => {
                  if (isDragging) return;
                  isActive ? openLightbox(site) : goToSlide(index);
                }}
                className="absolute w-[280px] md:w-[480px] cursor-pointer"
                style={{ 
                  transformStyle: "preserve-3d",
                  filter: style.filter,
                }}
              >
                <div className={`
                  relative overflow-hidden rounded-xl border transition-all duration-300
                  ${isActive 
                    ? 'border-indigo-500/50 shadow-[0_0_60px_rgba(99,102,241,0.3)]' 
                    : 'border-white/10'
                  }
                `}>
                  <div className="aspect-[16/10] overflow-hidden bg-[#111]">
                    <motion.img
                      src={site.image}
                      alt={`${site.name} website screenshot`}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                      animate={{
                        scale: isActive ? 1.08 : 1,
                      }}
                      transition={{
                        duration: 8,
                        ease: "linear",
                        repeat: isActive ? Infinity : 0,
                        repeatType: "reverse",
                      }}
                    />
                  </div>
                  
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                  
                  {/* Text content */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                    <h4 className="text-white font-semibold text-base md:text-lg drop-shadow-lg">{site.name}</h4>
                    <p className="text-white/60 text-xs md:text-sm">{site.type}</p>
                  </div>

                  {/* Click to expand hint for active card */}
                  {isActive && (
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="absolute top-4 right-4"
                    >
                      <span className="px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs font-medium flex items-center gap-1.5">
                        <ExternalLink className="w-3 h-3" />
                        Click to expand
                      </span>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Navigation arrows */}
        <button
          onClick={() => { setProgress(0); prevSlide(); }}
          className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300"
        >
          <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
        </button>
        
        <button
          onClick={() => { setProgress(0); nextSlide(); }}
          className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300"
        >
          <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
        </button>
      </motion.div>

      {/* Progress bar and dot indicators */}
      <div className="flex flex-col items-center gap-4 mt-6">
        {/* Progress bar */}
        <div className="w-48 h-1 bg-white/10 rounded-full overflow-hidden">
          <motion.div 
            className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full"
            style={{ width: `${progress}%` }}
            transition={{ duration: 0.05 }}
          />
        </div>
        
        {/* Dot indicators */}
        <div className="flex justify-center gap-2">
          {portfolioSites.map((_, index) => (
            <button
              key={index}
              onClick={() => handleManualNavigation(index)}
              className={`
                h-2 rounded-full transition-all duration-300
                ${index === activeIndex 
                  ? 'w-8 bg-indigo-500' 
                  : 'w-2 bg-white/20 hover:bg-white/40'
                }
              `}
            />
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedSite && (
          <Lightbox 
            site={selectedSite} 
            onClose={closeLightbox}
            onNext={lightboxNext}
            onPrev={lightboxPrev}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};
